import React, { useState, useEffect } from "react";
import axios from "axios";
import { CheckBox } from "../components/Input";
import { API_URL } from "../config";
import StateManagedSelect from "react-select";
import LoadingSpinner from "../components/LoadingSpinner";
import { PermissionWrapper, hasPermission } from "../helpers/permission.js";

const CHANNEL_MAP = [
  { key: "email", idKey: "email_id", label: "Email" },
  { key: "sms", idKey: "sms_id", label: "SMS" },
  { key: "push", idKey: "push_id", label: "Push Notification" },
  { key: "whatsapp", idKey: "whatsapp_id", label: "WhatsApp" },
];

const CampaignModal = ({ isOpen, onClose, onSubmit, loading, checkingAll, selectedCount }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateList, setTemplateList] = useState([]);
  const [channels, setChannels] = useState({});
  const [availableChannels, setAvailableChannels] = useState([]);
  const [startLimit, setStartLimit] = useState("");
  const [endLimit, setEndLimit] = useState("");
  const [campaignName, setCampaignName] = useState("");

  const [templateLoading, setTemplateLoading] = useState(false);

  // Fetch templates from API when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const fetchTemplates = async () => {
      try {
        setTemplateLoading(true);
        const res = await axios.get(
          `${API_URL}settings/configurationsTemplates/getSubscriberListCampaign`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              Accept: "*/*",
            },
          }
        );
        if (res.data?.templates) {
          setTemplateList(res.data.templates);
        } else {
          setTemplateList([]);
        }
      } catch (err) {
        console.error("Error fetching templates:", err);
        setTemplateList([]);
      } finally {
        setTemplateLoading(false);
      }
    };

    fetchTemplates();
  }, [isOpen]);

  // Reset form and auto-fill start/end limits when modal opens
  useEffect(() => {
    if (isOpen) {
      if (checkingAll && selectedCount) {
        setStartLimit("1");
        setEndLimit(selectedCount.toString());
      } else {
        setStartLimit("");
        setEndLimit("");
      }
      setSelectedTemplate(null);
      setChannels({});
    } else {
      setStartLimit("");
      setEndLimit("");
      setSelectedTemplate(null);
      setChannels({});
    }
  }, [isOpen, checkingAll, selectedCount]);

  // Update available channels when template changes
  useEffect(() => {
    if (!selectedTemplate) {
      setAvailableChannels([]);
      setChannels({});
      return;
    }
    const template = templateList.find((tpl) => tpl.id === selectedTemplate.value);
    if (!template) {
      setAvailableChannels([]);
      setChannels({});
      return;
    }

    const available = CHANNEL_MAP.filter(
      (ch) => template[ch.idKey] && template[ch.idKey] !== 0
    );
    setAvailableChannels(available);

    const defaultChannels = {};
    CHANNEL_MAP.forEach((ch) => {
      defaultChannels[ch.key] = available.some((av) => av.key === ch.key);
    });
    setChannels(defaultChannels);
  }, [selectedTemplate, templateList]);

  // Unified checkbox handler
  const handleCheckboxChange = (name, checked) => {
    setChannels((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedTemplate) {
      alert("Please select a template");
      return;
    }

    if (startLimit && endLimit) {
      const start = parseInt(startLimit);
      const end = parseInt(endLimit);
      if (start > end) {
        alert("Start limit cannot be greater than end limit");
        return;
      }
    }

    onSubmit({
      templateId: selectedTemplate.value,
      channels,
      startLimit: startLimit || undefined,
      endLimit: endLimit || undefined,
      campaign_name: campaignName || "",
    });
  };

  const handleStartLimitChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setStartLimit(value);
  };

  const handleEndLimitChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    setEndLimit(value);
  };

  if (!isOpen) return null;

  const templateOptions = templateList.map((tpl) => ({
    label: tpl.template_name,
    value: tpl.id,
  }));

  if (loading) return <LoadingSpinner />;

  // Calculate dynamic total records selected
  const calculatedCount =
    startLimit && endLimit && parseInt(endLimit) >= parseInt(startLimit)
      ? parseInt(endLimit) - parseInt(startLimit) + 1
      : selectedCount;

  return (
    <>
      <div className="modal-backdrop fade show"></div>
      <div
        className="modal fade show"
        style={{ display: "block" }}
        tabIndex="-1"
        role="dialog"
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <div className="col-md-6 d-flex align-items-center">
                <h5 className="modal-title" style={{ fontWeight: "bold", marginRight: "10px" }}>
                  Send Campaign
                </h5>
                <span
                  style={{
                    fontSize: "0.8rem",
                    color: "#666",
                    fontWeight: "normal"
                  }}
                  className="mt-1"
                >
                  ({checkingAll ? `All ${selectedCount}` : `${selectedCount}`} records selected)
                </span>
              </div>
              <div className="col-md-6">
                <button
                  type="button"
                  className="btn btn-default float-end"
                  onClick={onClose}
                >
                  &times;
                </button>
              </div>
              <div className="clearfix"></div>
            </div>

            <div className="modal-body">
              <form onSubmit={handleSubmit}>
                <div className="row">
                  {/* Campaign Name */}
                  <div className="col-md-12 mb-3">
                    <div className="form-group">
                      <label className="control-label">Campaign Name</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter campaign name"
                        value={campaignName}
                        onChange={(e) => setCampaignName(e.target.value)}
                        maxLength={255}
                      />
                    </div>
                  </div>

                  {/* Template Dropdown */}
                  <div className="col-md-12 mb-3">
                    <div className="form-group">
                      <label className="control-label">Select Template</label>
                      <StateManagedSelect
                        value={selectedTemplate}
                        onChange={setSelectedTemplate}
                        options={templateOptions}
                        placeholder={
                          templateLoading
                            ? "Loading templates..."
                            : templateOptions.length === 0
                            ? "No campaign templates configured"
                            : "-- Select Template --"
                        }
                        isDisabled={templateLoading}
                        isSearchable
                        styles={{
                          menu: (provided) => ({
                            ...provided,
                            maxHeight: 250,
                            overflowY: "auto",
                            overflowX: "hidden",
                          }),
                        }}
                      />
                    </div>
                  </div>

                  {/* Start/End Limit Fields - show only if checkingAll is true */}
                  {checkingAll && (
                    <>
                      <div className="col-md-6 mb-3">
                        <div className="form-group">
                          <label className="control-label">Start Limit</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter start limit"
                            value={startLimit}
                            onChange={handleStartLimitChange}
                            maxLength={10}
                          />
                          <small className="form-text text-muted">
                            Optional: Start from record number
                          </small>
                        </div>
                      </div>

                      <div className="col-md-6 mb-3">
                        <div className="form-group">
                          <label className="control-label">End Limit</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter end limit"
                            value={endLimit}
                            onChange={handleEndLimitChange}
                            maxLength={10}
                          />
                          <small className="form-text text-muted">
                            Optional: End at record number
                          </small>
                        </div>
                      </div>

                      {/* Display total records selected below limits */}
                      <div className="col-12 mb-3 text-end">
                        <strong>Total Records Selected: {calculatedCount}</strong>
                      </div>
                    </>
                  )}

                  {/* Channels Checkboxes */}
                  {availableChannels.length > 0 && (
                    <div className="col-md-12 mb-3">
                      <fieldset className="form-group">
                        <label className="form-label semibold">Channels</label>
                        <div className="d-flex flex-wrap gap-3 mt-2">
                          {availableChannels.map((ch) => (
                            <CheckBox
                              key={ch.key}
                              label={ch.label}
                              fieldname={ch.key}
                              checked={channels[ch.key] || false}
                              onChange={(e) =>
                                handleCheckboxChange(ch.key, e.target.checked)
                              }
                            />
                          ))}
                        </div>
                      </fieldset>
                    </div>
                  )}

                  {/* Submit Buttons */}
                  <div className="col-md-12">
                    <div style={{ float: "left", marginTop: "20px" }}>
                      {hasPermission("bulk_campaign", "add") ? (
                        <button
                          type="submit"
                          disabled={loading}
                          className="btn btn-primary mx-2"
                          title="Submit campaign"
                        >
                          {loading ? "Submitting..." : "Submit"}
                        </button>

                      ) : (
                        <button
                          type="button"
                          className="btn btn-primary mx-2"
                          disabled
                          title="You don't have permission to send campaigns"
                        >
                          Submit
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={onClose}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>

                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CampaignModal;
